import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import ChevronLeftOutlinedIcon from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightOutlinedIcon from "@mui/icons-material/ChevronRightOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { fetchVacationCalendar } from "../../../api/TimeTracking/timeTrackingApi";
import { localDateString } from "./dailyBoardUtils";
import { REQUEST_TYPE_LABEL } from "./vacationCalendarUtils";
import {
  ABSENCE_TYPES,
  WEEKDAY_LABELS,
  buildMonthAbsenceMap,
  buildWeekdayWeeks,
  countPending,
  formatMonthHeading,
  shiftMonth,
} from "./absencesMonthUtils";
import AbsenceAssignDialog from "./AbsenceAssignDialog";
import { ttFilterFieldSx } from "./timeTrackingStyles";
import {
  MobileEmptyState,
  MobileRecordCard,
  statusAccentFromMuiColor,
} from "./TimeTrackingMobileCards";

const SIDEBAR_WIDTH = 248;

function DayCell({ cell, items, theme, onAssign, onItemClick }) {
  if (!cell) {
    return (
      <Box
        sx={{
          minHeight: 88,
          bgcolor: alpha(theme.palette.action.hover, 0.2),
          borderRight: "1px solid",
          borderBottom: "1px solid",
          borderColor: alpha(theme.palette.divider, 0.45),
        }}
      />
    );
  }

  const isToday = cell.dateStr === localDateString();
  const visible = items.slice(0, 5);
  const extra = items.length - visible.length;

  return (
    <Box
      sx={{
        minHeight: 88,
        p: 0.75,
        borderRight: "1px solid",
        borderBottom: "1px solid",
        borderColor: alpha(theme.palette.divider, 0.45),
        bgcolor: isToday
          ? alpha(theme.palette.primary.main, 0.06)
          : "background.paper",
        cursor: onAssign ? "pointer" : "default",
        "&:hover": onAssign
          ? { bgcolor: alpha(theme.palette.primary.main, 0.04) }
          : undefined,
      }}
      onClick={onAssign ? () => onAssign(cell.dateStr) : undefined}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: isToday ? 800 : 700,
          color: isToday ? "primary.main" : "text.secondary",
        }}
      >
        {cell.day}
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
        {visible.map((item) => (
          <Tooltip
            key={item.id}
            title={`${item.username} · ${REQUEST_TYPE_LABEL[item.requestType] || item.requestType} · ${
              item.status === "pending" ? "Pendiente" : "Aprobada"
            }`}
            arrow
          >
            <Avatar
              onClick={
                onItemClick
                  ? (e) => {
                      e.stopPropagation();
                      onItemClick(item);
                    }
                  : undefined
              }
              sx={{
                width: 26,
                height: 26,
                fontSize: "0.62rem",
                fontWeight: 800,
                bgcolor: alpha(
                  item.color,
                  item.status === "pending" ? 0.2 : 0.9,
                ),
                color: item.status === "pending" ? item.color : "#fff",
                border: "2px solid",
                borderColor:
                  item.status === "pending"
                    ? alpha(item.color, 0.8)
                    : alpha(item.color, 0.95),
                borderStyle: item.status === "pending" ? "dashed" : "solid",
                cursor: onItemClick ? "pointer" : "default",
              }}
            >
              {item.initials}
            </Avatar>
          </Tooltip>
        ))}
        {extra > 0 ? (
          <Chip
            label={`+${extra}`}
            size="small"
            sx={{ height: 22, fontWeight: 700 }}
          />
        ) : null}
      </Stack>
    </Box>
  );
}

const AbsencesMonthTab = ({
  selectedUserId,
  canRrhh,
  canFichaje,
  employees = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [search, setSearch] = useState("");
  const [typeFilters, setTypeFilters] = useState(
    () => new Set(ABSENCE_TYPES.map((t) => t.value)),
  );
  const [showApproved, setShowApproved] = useState(true);
  const [showPending, setShowPending] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignDate, setAssignDate] = useState("");
  const [editingAbsence, setEditingAbsence] = useState(null);

  const statusParam = useMemo(() => {
    if (!canRrhh) return "approved";
    const parts = [];
    if (showApproved) parts.push("approved");
    if (showPending) parts.push("pending");
    return parts.length ? parts.join(",") : "approved";
  }, [canRrhh, showApproved, showPending]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        year,
        requestType: "all",
        status: statusParam,
      };
      if (selectedUserId) params.userId = selectedUserId;
      const result = await fetchVacationCalendar(params);
      setData(result);
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo cargar las ausencias");
    } finally {
      setLoading(false);
    }
  }, [year, statusParam, selectedUserId]);

  useEffect(() => {
    load();
  }, [load]);

  const byDate = useMemo(
    () =>
      buildMonthAbsenceMap(
        data?.entries || [],
        {
          types: typeFilters,
          showApproved,
          showPending: canRrhh ? showPending : false,
          search,
        },
        year,
        month,
      ),
    [
      data,
      typeFilters,
      showApproved,
      showPending,
      canRrhh,
      search,
      year,
      month,
    ],
  );

  const weeks = useMemo(() => buildWeekdayWeeks(year, month), [year, month]);
  const pendingCount = countPending(data?.entries || []);

  const mobileDays = useMemo(() => {
    const days = [];
    weeks.forEach((week) => {
      week.forEach((cell) => {
        if (!cell) return;
        const items = byDate[cell.dateStr] || [];
        if (items.length) {
          days.push({ ...cell, items });
        }
      });
    });
    return days;
  }, [weeks, byDate]);

  const toggleType = (value) => {
    setTypeFilters((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const openAssign = (dateStr = "") => {
    setEditingAbsence(null);
    setAssignDate(dateStr);
    setAssignOpen(true);
  };

  const openEdit = (item) => {
    setEditingAbsence({
      id: item.requestId,
      userId: item.userId,
      requestType: item.requestType,
      startDate: item.startDate,
      endDate: item.endDate,
      reason: item.reason || "",
    });
    setAssignDate(item.startDate || "");
    setAssignOpen(true);
  };

  const changeMonth = (delta) => {
    const next = shiftMonth(year, month, delta);
    setYear(next.year);
    setMonth(next.month);
  };

  return (
    <Box>
      {error ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1}
        alignItems={{ md: "center" }}
        sx={{ mb: 1.5 }}
      >
        <TextField
          size="small"
          placeholder="Buscar empleado"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ ...ttFilterFieldSx(theme), width: { xs: "100%", sm: 240 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconButton size="small" onClick={() => changeMonth(-1)}>
            <ChevronLeftOutlinedIcon />
          </IconButton>
          <Chip
            label={formatMonthHeading(year, month)}
            sx={{ fontWeight: 800, minWidth: 160, textTransform: "capitalize" }}
          />
          <IconButton size="small" onClick={() => changeMonth(1)}>
            <ChevronRightOutlinedIcon />
          </IconButton>
        </Stack>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshOutlinedIcon />}
          onClick={load}
          disabled={loading}
          sx={{ fontWeight: 700 }}
        >
          Actualizar
        </Button>
        <Box sx={{ flex: 1 }} />
        {canRrhh || canFichaje ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddOutlinedIcon />}
            onClick={() => openAssign()}
            sx={{ fontWeight: 700, width: { xs: "100%", md: "auto" } }}
          >
            {canRrhh ? "Asignar ausencia" : "Solicitar ausencia"}
          </Button>
        ) : null}
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {/* Filtros compactos en móvil */}
          <Box
            sx={{
              p: 1.25,
              borderRadius: 2.5,
              border: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.65),
              bgcolor: alpha(theme.palette.background.paper, 0.7),
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
              Tipos de ausencias
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1 }}>
              {ABSENCE_TYPES.map((t) => (
                <Chip
                  key={t.value}
                  label={t.label}
                  size="small"
                  onClick={() => toggleType(t.value)}
                  color={typeFilters.has(t.value) ? "primary" : "default"}
                  variant={typeFilters.has(t.value) ? "filled" : "outlined"}
                  sx={{ fontWeight: 700, fontSize: "0.68rem" }}
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={1}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={showApproved}
                    onChange={(e) => setShowApproved(e.target.checked)}
                  />
                }
                label={<Typography variant="caption">Aceptadas</Typography>}
              />
              {canRrhh ? (
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={showPending}
                      onChange={(e) => setShowPending(e.target.checked)}
                    />
                  }
                  label={<Typography variant="caption">Pendientes</Typography>}
                />
              ) : null}
            </Stack>
          </Box>

          {mobileDays.map((day) => (
            <MobileRecordCard
              key={day.dateStr}
              theme={theme}
              accent={theme.palette.primary.main}
              title={formatMonthHeading(year, month).replace(/^\w+\s/, "")}
              subtitle={`Día ${day.day} · ${day.dateStr}`}
              statusLabel={`${day.items.length} ausencia(s)`}
              statusColor="info"
            >
              <Stack spacing={0.75}>
                {day.items.map((item) => {
                  const status =
                    item.status === "pending" ? "warning" : "success";
                  return (
                    <Box
                      key={item.id}
                      onClick={
                        canRrhh
                          ? () => openEdit(item)
                          : canFichaje
                            ? () => openAssign(day.dateStr)
                            : undefined
                      }
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1,
                        borderRadius: 1.75,
                        bgcolor: alpha(item.color, 0.08),
                        border: "1px solid",
                        borderColor: alpha(item.color, 0.22),
                        cursor: canRrhh || canFichaje ? "pointer" : "default",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 30,
                          height: 30,
                          fontSize: "0.65rem",
                          fontWeight: 800,
                          bgcolor: alpha(
                            item.color,
                            item.status === "pending" ? 0.2 : 0.9,
                          ),
                          color:
                            item.status === "pending" ? item.color : "#fff",
                        }}
                      >
                        {item.initials}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {item.username}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {REQUEST_TYPE_LABEL[item.requestType] ||
                            item.requestType}
                        </Typography>
                      </Box>
                      <Chip
                        label={
                          item.status === "pending" ? "Pendiente" : "Aprobada"
                        }
                        size="small"
                        sx={{
                          height: 22,
                          fontWeight: 700,
                          fontSize: "0.65rem",
                          bgcolor: alpha(
                            statusAccentFromMuiColor(theme, status),
                            0.12,
                          ),
                          color: statusAccentFromMuiColor(theme, status),
                        }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </MobileRecordCard>
          ))}
          {!mobileDays.length ? (
            <MobileEmptyState>Sin ausencias en este mes</MobileEmptyState>
          ) : null}
        </Stack>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.85),
            overflow: "hidden",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* Sidebar filtros */}
          <Box
            sx={{
              width: { xs: "100%", md: SIDEBAR_WIDTH },
              flexShrink: 0,
              borderRight: { md: "1px solid" },
              borderColor: { md: alpha(theme.palette.divider, 0.75) },
              bgcolor: alpha(theme.palette.background.paper, 0.6),
              p: 1.5,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Tipos de ausencias
            </Typography>
            <Stack spacing={0.25} sx={{ mb: 1.5 }}>
              {ABSENCE_TYPES.map((t) => (
                <FormControlLabel
                  key={t.value}
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={typeFilters.has(t.value)}
                      onChange={() => toggleType(t.value)}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
                      {t.label}
                    </Typography>
                  }
                />
              ))}
            </Stack>

            <Divider sx={{ my: 1.25 }} />

            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Solicitudes
              </Typography>
              {canRrhh && pendingCount > 0 ? (
                <Chip
                  label={pendingCount}
                  size="small"
                  color="error"
                  sx={{
                    height: 22,
                    minWidth: 22,
                    fontWeight: 800,
                    fontSize: "0.72rem",
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              ) : null}
            </Stack>
            <FormControlLabel
              sx={{ m: 0, display: "flex" }}
              control={
                <Checkbox
                  size="small"
                  checked={showApproved}
                  onChange={(e) => setShowApproved(e.target.checked)}
                />
              }
              label="Aceptadas"
            />
            {canRrhh ? (
              <FormControlLabel
                sx={{ m: 0, display: "flex" }}
                control={
                  <Checkbox
                    size="small"
                    checked={showPending}
                    onChange={(e) => setShowPending(e.target.checked)}
                  />
                }
                label="Pendientes"
              />
            ) : null}
          </Box>

          {/* Calendario mensual L–V */}
          <Box
            sx={{
              flex: 1,
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <Box sx={{ minWidth: 600 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(5, minmax(120px, 1fr))",
                  borderBottom: "2px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.2),
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                }}
              >
                {WEEKDAY_LABELS.map((label) => (
                  <Box
                    key={label}
                    sx={{
                      py: 1,
                      px: 1,
                      fontWeight: 800,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      color: "text.secondary",
                      borderRight: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.45),
                    }}
                  >
                    {label}
                  </Box>
                ))}
              </Box>

              {weeks.map((week, wi) => (
                <Box
                  key={`week-${wi}`}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(5, minmax(120px, 1fr))",
                  }}
                >
                  {week.map((cell, ci) => (
                    <DayCell
                      key={cell?.dateStr || `empty-${wi}-${ci}`}
                      cell={cell}
                      items={cell ? byDate[cell.dateStr] || [] : []}
                      theme={theme}
                      onAssign={canRrhh || canFichaje ? openAssign : null}
                      onItemClick={canRrhh ? openEdit : null}
                    />
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      )}

      <AbsenceAssignDialog
        open={assignOpen}
        onClose={() => {
          setAssignOpen(false);
          setEditingAbsence(null);
        }}
        onSaved={load}
        canRrhh={canRrhh}
        employees={employees}
        defaultDate={assignDate}
        existing={editingAbsence}
      />
    </Box>
  );
};

export default AbsencesMonthTab;
